import { Link } from '@chakra-ui/react';
import React, { Fragment } from 'react';

// Regex constants
const URL_REGEX = /https?:\/\/[^\s"']+/g;
const IMAGE_EXT_REGEX = /\.(?:jpg|jpeg|png|gif|bmp|tiff|ico|webp|svg|heic|heif)(?:\?.*)?$/i;
const MD_IMAGE_REGEX = /!\[([^\]]*)\]\((https?:\/\/[^\s)]+)\)/g;
const MD_LINK_REGEX = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;

export const MARKDOWN_IMAGE_PATTERN = /!\[([^\]]*)\]\(([^)]+)\)/;

export const REMOVE_MARKDOWN_IMAGE_PATTERN = /!\[([^\]]*)\]\(([^)]+)\)/g;

export const FILE_NAME_PATTERN = /([\w\s\-_()]+\.(?:jpg|jpeg|png|gif|webp|svg|bmp|pdf|docx|txt|csv))/i;

type MatchBase = { index: number; length: number; type: 'link' | 'image'; url: string };
type LinkMatch = MatchBase & { type: 'link'; label?: string };
type ImageMatch = MatchBase & { type: 'image'; alt?: string };
type AnyMatch = LinkMatch | ImageMatch;

export const linkifyText = (text: string) => {
  const occupied: Array<[number, number]> = [];
  const matches: AnyMatch[] = [];

  // 1) Markdown images
  for (const m of text.matchAll(MD_IMAGE_REGEX)) {
    const alt = m[1];
    const url = m[2];
    const index = m.index ?? 0;
    matches.push({ type: 'image', url, alt, index, length: m[0].length });
    occupied.push([index, index + m[0].length]);
  }

  // 2) Markdown links (skip those that were like ![...])
  for (const m of text.matchAll(MD_LINK_REGEX)) {
    const index = m.index ?? 0;
    if (index > 0 && text[index - 1] === '!') {continue;} // this was ![...]
    const label = m[1];
    const url = m[2];
    matches.push({ type: 'link', url, label, index, length: m[0].length });
    occupied.push([index, index + m[0].length]);
  }

  const overlaps = (start: number, end: number) =>
    occupied.some(([s, e]) => start < e && end > s);

  // 3) Ordinary URLs (and images by extension), which are not inside markdown
  for (const m of text.matchAll(URL_REGEX)) {
    const url = m[0];
    const index = m.index ?? 0;
    const end = index + url.length;
    if (overlaps(index, end)) {continue;}

    const isImage = IMAGE_EXT_REGEX.test(url);
    matches.push({ type: isImage ? 'image' : 'link', url, index, length: url.length });
  }

  if (matches.length === 0) {return <Fragment>{text}</Fragment>;}

  matches.sort((a, b) => a.index - b.index);

  const out: React.ReactNode[] = [];
  let last = 0;
  let key = 0;

  for (const m of matches) {
    if (m.index > last) {out.push(text.slice(last, m.index));}

    if (m.type === 'image') {
      out.push(
        <img
          key={`img-${key++}`}
          src={m.url}
          alt={('alt' in m && m.alt) ? m.alt! : 'image'}
          style={{ width: '100%', height: 'auto', maxHeight: '700px', objectFit: 'contain' }}
        />
      );
    } else {
      out.push(
        <Link
          key={`link-${key++}`}
          href={m.url}
          target="_blank"
          rel="noopener noreferrer"
          _hover={{ textDecoration: 'underline' }}
          color="blue.600"
        >
          {('label' in m && m.label) ? m.label! : m.url}
        </Link>
      );
    }

    last = m.index + m.length;
  }

  if (last < text.length) {out.push(text.slice(last));}

  return out;
};

export const truncateText = (text: string, maxLength: number = 100) => {
  if (text.length <= maxLength) {
    return text;
  }

  return text.substring(0, maxLength) + '...';
};